import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Text, VStack, HStack, Input, InputGroup, InputLeftElement,
    Badge, Spinner, Divider, IconButton, Tooltip, useToast, Select, Avatar, Button,
    Image as ChakraImage, Heading, Icon, useMediaQuery,
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
    FaEnvelope, FaEnvelopeOpen, FaSync, FaRegClock, FaCheckCircle, FaComments, FaExternalLinkAlt,
    FaFlagCheckered, FaUndoAlt, FaThumbsUp, FaStopwatch, FaBan,
} from 'react-icons/fa';
import { IoMdCart } from 'react-icons/io';
import { GoPackage } from 'react-icons/go';
import { MdLocalShipping, MdCancel } from 'react-icons/md';
import {
    getMensageriaConversas,
    marcarTodasMensagensLidas,
    marcarTodasMensagensNaoLidas,
    marcarMensagemLida,
    marcarMensagemNaoLida,
    finalizarConversaMensageria,
    reabrirConversaMensageria,
    getMensageriaMetricas,
} from '../services/api';
import ChatMensagensML from '../components/ChatMensagensML';
import MensageriaKanban from '../components/MensageriaKanban';
import { AlternarVisao } from '../components/VendasKanban';
import ScrollToTop from '../components/ScrollToTop';
import FiltroMultiplo from '../components/FiltroMultiplo';
import { useDebounce } from '../hooks/useDebounce';
import { DateTime } from 'luxon';

const PER_PAGE = 25;

// Mesmo esquema visual usado no VendaItem (cor + icone por status da venda).
const STATUS_INFO = {
    confirmada: { label: 'CONFIRMADA',  bg: 'gray.600',  color: 'white', Icon: IoMdCart },
    enviar:     { label: 'PARA ENVIAR', bg: 'yellow.200', color: 'black', Icon: GoPackage },
    transito:   { label: 'EM TRÂNSITO', bg: 'blue.500',  color: 'white', Icon: MdLocalShipping },
    concluida:  { label: 'CONCLUÍDA',   bg: 'green.500', color: 'white', Icon: FaCheckCircle },
    cancelada:  { label: 'CANCELADA',   bg: 'red.500',   color: 'white', Icon: MdCancel },
};
const statusInfo = (s) => STATUS_INFO[String(s || '').toLowerCase()] || null;

const OPCOES_STATUS = Object.entries(STATUS_INFO).map(([valor, { label }]) => ({
    valor,
    rotulo: label.charAt(0) + label.slice(1).toLowerCase(),
}));
const TODOS_OS_STATUS = OPCOES_STATUS.map(o => o.valor);

const tempoEspera = (iso) => {
    if (!iso) return '';
    const d = DateTime.fromISO(iso);
    if (!d.isValid) return '';
    const mins = Math.max(0, Math.floor(DateTime.now().diff(d, 'minutes').minutes));
    if (mins < 60) return `${mins}min`;
    const horas = Math.floor(mins / 60);
    if (horas < 24) return `${horas}h`;
    return `${Math.floor(horas / 24)}d`;
};

const horasDesde = (iso) => {
    if (!iso) return 0;
    const d = DateTime.fromISO(iso);
    return d.isValid ? DateTime.now().diff(d, 'hours').hours : 0;
};

const formatBR = (iso) => {
    if (!iso) return '';
    const d = DateTime.fromISO(iso).setZone('America/Sao_Paulo');
    return d.isValid ? d.toFormat('dd/MM HH:mm') : '';
};

// Chip de ID clicável: copia o valor para a área de transferência ao clicar.
const IdChip = ({ label, value, onCopiar }) => {
    if (value == null || value === '') return null;
    return (
        <Tooltip label={`Copiar ${label}`} openDelay={400}>
            <Flex
                as="button" type="button"
                onClick={(e) => { e.stopPropagation(); onCopiar(label, value); }}
                align="center" gap="3px"
                bg="gray.100" _hover={{ bg: 'gray.200' }}
                px={1.5} py="2px" borderRadius="md"
                fontSize="10px" fontWeight="medium" color="gray.600"
                transition="background 0.12s"
            >
                <Text as="span" color="gray.400" fontWeight="semibold">{label}</Text>
                {value}
            </Flex>
        </Tooltip>
    );
};

// Ícone "abrir em nova aba": leva a outra página já filtrada (produto / cliente).
const AbrirLink = ({ label, href }) => (
    <Tooltip label={label} openDelay={400}>
        <Flex
            as="button" type="button"
            onClick={(e) => { e.stopPropagation(); window.open(href, '_blank', 'noopener'); }}
            align="center" justify="center"
            boxSize="18px" borderRadius="md" flexShrink={0}
            color="gray.400" _hover={{ bg: 'gray.200', color: 'orange.500' }}
            transition="all 0.12s"
        >
            <FaExternalLinkAlt size={9} />
        </Flex>
    </Tooltip>
);

// Botão para marcar uma conversa individual como lida / não lida.
const BotaoLeitura = ({ naoLida, onToggle }) => (
    <Tooltip label={naoLida ? 'Marcar como lida' : 'Marcar como não lida'} openDelay={400}>
        <Flex
            as="button" type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            align="center" justify="center"
            boxSize="24px" borderRadius="md" flexShrink={0}
            color={naoLida ? 'blue.500' : 'gray.400'}
            _hover={{
                bg: naoLida ? 'blue.50' : 'gray.100',
                color: naoLida ? 'blue.600' : 'gray.600',
            }}
            transition="all 0.12s"
        >
            {naoLida ? <FaEnvelopeOpen size={13} /> : <FaEnvelope size={13} />}
        </Flex>
    </Tooltip>
);

// Finaliza a conversa (sai da fila; reabre sozinha se o comprador escrever) ou desfaz.
const BotaoFinalizar = ({ finalizada, onToggle }) => (
    <Tooltip label={finalizada ? 'Desfazer finalização' : 'Finalizar conversa (sai da fila de pendentes)'} openDelay={400}>
        <Flex
            as="button" type="button"
            aria-label={finalizada ? 'Desfazer finalização' : 'Finalizar conversa'}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            align="center" justify="center"
            boxSize="24px" borderRadius="md" flexShrink={0}
            color={finalizada ? 'purple.500' : 'gray.400'}
            _hover={{
                bg: finalizada ? 'purple.50' : 'gray.100',
                color: finalizada ? 'purple.600' : 'gray.600',
            }}
            transition="all 0.12s"
        >
            {finalizada ? <FaUndoAlt size={12} /> : <FaFlagCheckered size={13} />}
        </Flex>
    </Tooltip>
);

// "3h 07min" a partir de segundos.
const formatDuracao = (seg) => {
    if (seg == null) return '—';
    const min = Math.round(seg / 60);
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    const resto = min % 60;
    return resto ? `${h}h ${String(resto).padStart(2, '0')}min` : `${h}h`;
};

// Indicador de SLA no topo da tela.
const CartaoSla = ({ titulo, valor, detalhe, nota, notaAjuda, cor, icone: IconeSla, carregando }) => (
    <Flex
        flex="1 1 200px" minW={0} gap={3} align="center"
        bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl"
        boxShadow="sm" px={4} py={3}
    >
        <Flex bg={`${cor}.50`} color={`${cor}.500`} boxSize={10} borderRadius="lg" align="center" justify="center" flexShrink={0}>
            <IconeSla size={16} />
        </Flex>
        <Box minW={0}>
            <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                {titulo}
            </Text>
            {carregando ? (
                <Spinner size="sm" color="gray.400" mt={1} />
            ) : (
                <Text fontSize="xl" fontWeight="bold" color={`${cor}.600`} lineHeight="1.2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {valor}
                </Text>
            )}
            <Text fontSize="11px" color="gray.500" noOfLines={2}>{detalhe}</Text>
            {nota && (
                <Tooltip label={notaAjuda} openDelay={300}>
                    <Text
                        fontSize="10px" color="gray.400" mt="1px"
                        borderBottomWidth="1px" borderBottomStyle="dashed" borderColor="gray.300"
                        display="inline-block" cursor="help"
                    >
                        {nota}
                    </Text>
                </Tooltip>
            )}
        </Box>
    </Flex>
);

// Rótulo pequeno acima de um campo de filtro.
const CampoLabel = ({ children }) => (
    <Text
        fontSize="10px" fontWeight="bold" color="gray.500"
        textTransform="uppercase" letterSpacing="wider" mb="3px"
    >
        {children}
    </Text>
);

// Badge de status (leitura / resposta) exibido em cada conversa.
const StatusPill = ({ bg, color, icon: PillIcon, children }) => (
    <Flex
        align="center" gap="3px"
        bg={bg} color={color}
        px={1.5} py="2px" borderRadius="md"
        fontSize="9px" fontWeight="bold"
        flexShrink={0}
    >
        <PillIcon size={8} />
        {children}
    </Flex>
);

const MOTIVO_BLOQUEIO = {
    venda_cancelada: 'Venda cancelada e sem conversa aberta: o Mercado Livre bloqueia o envio',
    cancelada_prazo_vencido: 'Venda cancelada: o prazo de 30 dias para responder o comprador venceu',
    sem_opcao_no_ml: 'O Mercado Livre não oferece nenhuma opção de mensagem para esta venda',
};

// `compacto` (coluna do kanban): mesmo conteúdo e mesmas ações; o selo de
// status e a data descem para a linha de baixo para caber na largura.
const ConversaCard = ({ conversa, selecionada, onClick, onCopiarId, onToggleLeitura, onToggleFinalizar, compacto = false }) => {
    const naoLida = !!conversa.nao_lida;
    const qtd = conversa.qtd || 0;
    const aguardando = !!conversa.aguardando_resposta;
    const finalizada = !!conversa.finalizada;
    const soAgradecimento = !!conversa.so_agradecimento;
    // O ML barra mensagem em venda cancelada (reabre por 30 dias se o comprador
    // escrever). Conversa barrada nao e pendencia de atendimento: ninguem pode
    // resolver, entao ela nao pode aparecer como "sem responder".
    const bloqueada = conversa.respondivel === false;
    const nomeExibicao = conversa.nome_cliente || conversa.nickname_cliente
        || conversa.nome_comprador || 'Cliente';
    const buscaCliente = conversa.nome_cliente || conversa.nickname_cliente || '';

    const ultimaMsg = conversa.ultimo_texto
        ? conversa.ultimo_texto.slice(0, 100) + (conversa.ultimo_texto.length > 100 ? '…' : '')
        : '—';

    const horas = horasDesde(conversa.ultima_msg_at);
    const esperaCor = horas >= 24 ? 'red.500' : horas >= 4 ? 'orange.500' : 'gray.500';

    const info = statusInfo(conversa.status);
    const prod = conversa.produto;

    const seloStatus = info && (
        <Flex
            align="center" gap="3px"
            bg={info.bg} color={info.color}
            px={1.5} py={0.5} borderRadius="md"
            fontSize="9px" fontWeight="bold"
        >
            <info.Icon size={11} />
            {info.label}
        </Flex>
    );
    const dataUltimaMsg = <Text fontSize="10px" color="gray.400">{formatBR(conversa.ultima_msg_at)}</Text>;

    return (
        <Flex
            px={compacto ? 3 : 4} py={3}
            bg={selecionada ? 'orange.50' : naoLida ? 'blue.50' : 'white'}
            borderWidth="1px"
            borderColor={selecionada ? 'orange.300' : naoLida ? 'blue.200' : 'gray.200'}
            borderLeftWidth="3px"
            borderLeftColor={selecionada ? '#F68633' : naoLida ? '#3182CE' : 'gray.300'}
            borderRadius="lg"
            boxShadow="xs"
            cursor="pointer"
            _hover={{
                boxShadow: 'md',
                borderColor: selecionada ? 'orange.300' : 'gray.300',
                bg: selecionada ? 'orange.50' : naoLida ? 'blue.50' : 'gray.50',
            }}
            onClick={onClick}
            align="flex-start"
            gap={3}
            transition="all 0.15s"
        >
            <Avatar
                size="sm"
                name={nomeExibicao}
                src="/client.webp"
                flexShrink={0}
            />
            <Box flex={1} minW={0}>
                <Flex justify="space-between" align="center" gap={2}>
                    <HStack spacing={1} minW={0}>
                        <Text fontSize="13px" fontWeight={naoLida ? 'bold' : 'semibold'} noOfLines={1}>
                            {nomeExibicao}
                        </Text>
                        {buscaCliente && (
                            <AbrirLink
                                label="Abrir cliente"
                                href={`/clientes?search=${encodeURIComponent(buscaCliente)}`}
                            />
                        )}
                    </HStack>
                    <HStack spacing={2} flexShrink={0}>
                        {!compacto && seloStatus}
                        {!compacto && dataUltimaMsg}
                        <BotaoFinalizar finalizada={finalizada} onToggle={onToggleFinalizar} />
                        <BotaoLeitura naoLida={naoLida} onToggle={onToggleLeitura} />
                    </HStack>
                </Flex>

                {compacto && (
                    <HStack spacing={2} mt={1} flexWrap="wrap">
                        {seloStatus}
                        {dataUltimaMsg}
                    </HStack>
                )}

                <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
                    {naoLida ? (
                        <StatusPill bg="blue.100" color="blue.700" icon={FaEnvelope}>Não lida</StatusPill>
                    ) : (
                        <StatusPill bg="gray.100" color="gray.500" icon={FaEnvelopeOpen}>Lida</StatusPill>
                    )}
                    {aguardando ? (
                        <StatusPill bg="orange.100" color="orange.700" icon={FaRegClock}>Sem responder</StatusPill>
                    ) : bloqueada ? (
                        <Tooltip label={MOTIVO_BLOQUEIO[conversa.motivo_bloqueio] || 'O Mercado Livre não aceita mais mensagem nesta conversa'} openDelay={300}>
                            <Box><StatusPill bg="gray.200" color="gray.600" icon={FaBan}>Sem resposta possível</StatusPill></Box>
                        </Tooltip>
                    ) : finalizada ? (
                        <Tooltip label={`Finalizada em ${formatBR(conversa.finalizada_at)} · reabre se o comprador escrever`} openDelay={300}>
                            <Box><StatusPill bg="purple.100" color="purple.700" icon={FaFlagCheckered}>Finalizada</StatusPill></Box>
                        </Tooltip>
                    ) : soAgradecimento ? (
                        <Tooltip label="O comprador só agradeceu ou confirmou: não precisa de resposta" openDelay={300}>
                            <Box><StatusPill bg="teal.100" color="teal.700" icon={FaThumbsUp}>Só agradecimento</StatusPill></Box>
                        </Tooltip>
                    ) : (
                        <StatusPill bg="green.100" color="green.700" icon={FaCheckCircle}>Respondida</StatusPill>
                    )}
                </HStack>

                <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
                    <IdChip label="TAGA" value={conversa.taga_id} onCopiar={onCopiarId} />
                    <IdChip label="ML" value={conversa.id_origem} onCopiar={onCopiarId} />
                    <IdChip label="Pack" value={conversa.pack_id_ml} onCopiar={onCopiarId} />
                    {conversa.id_origem && (
                        <AbrirLink
                            label="Abrir venda"
                            href={`/vendas?busca=${encodeURIComponent(conversa.id_origem)}`}
                        />
                    )}
                </HStack>

                <Text fontSize="11px" color="gray.600" noOfLines={1} mt={1}>
                    {conversa.ultima_msg_taga ? 'Você: ' : ''}{ultimaMsg}
                </Text>

                {prod && (
                    <HStack
                        mt={2} spacing={2} align="center"
                        bg="gray.50" p={1.5} borderRadius="md"
                        borderWidth="1px" borderColor="gray.100"
                    >
                        {prod.imagem ? (
                            <ChakraImage
                                src={prod.imagem}
                                boxSize="32px" objectFit="cover" borderRadius="sm"
                                fallback={<Box boxSize="32px" bg="gray.200" borderRadius="sm" />}
                            />
                        ) : (
                            <Box boxSize="32px" bg="gray.200" borderRadius="sm" />
                        )}
                        <Box minW={0} flex={1}>
                            <Text fontSize="11px" fontWeight="medium" noOfLines={1}>{prod.nome}</Text>
                            <HStack spacing={1.5} mt="3px" flexWrap="wrap">
                                <IdChip label="SKU" value={prod.sku} onCopiar={onCopiarId} />
                                {prod.sku && (
                                    <AbrirLink
                                        label="Abrir produto"
                                        href={`/produtos?id=${encodeURIComponent(prod.sku)}`}
                                    />
                                )}
                                <Text fontSize="10px" color="gray.500">{prod.quantidade}x</Text>
                            </HStack>
                        </Box>
                    </HStack>
                )}

                {aguardando && (
                    <HStack spacing={1} mt={1} color={esperaCor}>
                        <FaRegClock size={9} />
                        <Text fontSize="10px" fontWeight="semibold">
                            aguardando há {tempoEspera(conversa.ultima_msg_at)}
                        </Text>
                    </HStack>
                )}
            </Box>

            {naoLida && (
                <Flex
                    flexShrink={0}
                    minW="20px" h="20px" px={1}
                    bg="red.500" color="white"
                    borderRadius="full"
                    fontSize="10px" fontWeight="bold"
                    align="center" justify="center"
                    mt={1}
                >
                    {qtd > 0 ? (qtd > 99 ? '99+' : qtd) : '•'}
                </Flex>
            )}
        </Flex>
    );
};

const Mensageria = () => {
    const toast = useToast({ position: 'top-right', duration: 3000 });

    const [conversas, setConversas] = useState([]);
    const [meta, setMeta] = useState({ total: 0, totalPaginas: 1, totalNaoLidas: 0 });
    const [loading, setLoading] = useState(false);
    const [loadingMassa, setLoadingMassa] = useState(false);

    const [busca, setBusca] = useState('');
    const buscaDebounced = useDebounce(busca, 400);
    const [filtroLeitura, setFiltroLeitura] = useState('todas');
    const [filtroResposta, setFiltroResposta] = useState('sem_responder');
    const [ordenar, setOrdenar] = useState('recentes');
    const [filtroStatus, setFiltroStatus] = useState(TODOS_OS_STATUS);
    const [page, setPage] = useState(1);

    const [selecionada, setSelecionada] = useState(null);
    const [chatOpen, setChatOpen] = useState(false);

    // Layout: lista (padrão) ou kanban. Só muda a forma de mostrar: mesmos
    // filtros, mesma ordem e mesmas ações nos dois.
    const [visaoKanban, setVisaoKanban] = useState(() => {
        try {
            return localStorage.getItem('taga_mensageria_kanban') === '1';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('taga_mensageria_kanban', visaoKanban ? '1' : '0');
        } catch {
        }
    }, [visaoKanban]);

    const [isMobile] = useMediaQuery('(max-width: 768px)', { ssr: false });
    const porColuna = isMobile ? 10 : 30;
    const [agruparKanban, setAgruparKanban] = useState('situacao');
    const [kanban, setKanban] = useState(null);
    const [limitesKanban, setLimitesKanban] = useState({});

    const [diasSla, setDiasSla] = useState(30);
    const [sla, setSla] = useState(null);
    const [loadingSla, setLoadingSla] = useState(false);

    const carregarSla = useCallback(async () => {
        setLoadingSla(true);
        try {
            setSla(await getMensageriaMetricas(diasSla));
        } catch {
            setSla(null);
        } finally {
            setLoadingSla(false);
        }
    }, [diasSla]);

    useEffect(() => { carregarSla(); }, [carregarSla]);

    const carregarConversas = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getMensageriaConversas({
                filtro_leitura: filtroLeitura, filtro_resposta: filtroResposta,
                status: filtroStatus, ordenar, busca: buscaDebounced,
                page, per_page: PER_PAGE,
            });
            setConversas(Array.isArray(res?.data) ? res.data : []);
            setMeta({
                total: res?.total || 0,
                totalPaginas: res?.total_paginas || 1,
                totalNaoLidas: res?.total_nao_lidas || 0,
            });
        } catch {
            toast({ title: 'Erro ao carregar conversas', status: 'error' });
        } finally {
            setLoading(false);
        }
        // toast fica fora das deps de proposito: useToast recria a ref a cada
        // render e causaria loop infinito de fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroLeitura, filtroResposta, filtroStatus, ordenar, buscaDebounced, page]);

    // Kanban: mesmos filtros e mesma ordem da lista, repartidos em colunas
    // pelo backend; cada coluna traz o total e as primeiras conversas.
    const carregarKanban = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getMensageriaConversas({
                filtro_leitura: filtroLeitura, filtro_resposta: filtroResposta,
                status: filtroStatus, ordenar, busca: buscaDebounced,
                agrupar: agruparKanban, por_coluna: porColuna,
                limites: Object.entries(limitesKanban).map(([chave, qtd]) => `${chave}:${qtd}`).join(','),
            });
            setKanban({ colunas: res?.colunas || {}, total: res?.total || 0, agrupar: agruparKanban });
            setMeta(m => ({ ...m, total: res?.total || 0, totalNaoLidas: res?.total_nao_lidas || 0 }));
        } catch {
            toast({ title: 'Erro ao carregar conversas', status: 'error' });
        } finally {
            setLoading(false);
        }
        // toast fora das deps de proposito (vide carregarConversas).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroLeitura, filtroResposta, filtroStatus, ordenar, buscaDebounced, agruparKanban, porColuna, limitesKanban]);

    const recarregar = visaoKanban ? carregarKanban : carregarConversas;

    useEffect(() => { if (!visaoKanban) carregarConversas(); }, [carregarConversas, visaoKanban]);
    useEffect(() => { if (visaoKanban) carregarKanban(); }, [carregarKanban, visaoKanban]);

    useEffect(() => {
        const interval = setInterval(visaoKanban ? carregarKanban : carregarConversas, 60000);
        return () => clearInterval(interval);
    }, [visaoKanban, carregarKanban, carregarConversas]);

    useEffect(() => { setPage(1); }, [buscaDebounced, filtroLeitura, filtroResposta, filtroStatus, ordenar]);

    // Filtro ou agrupamento novo: cada coluna volta a mostrar só o primeiro lote.
    useEffect(() => {
        setLimitesKanban(l => (Object.keys(l).length ? {} : l));
    }, [buscaDebounced, filtroLeitura, filtroResposta, filtroStatus, ordenar, agruparKanban, porColuna]);

    const patchConversa = (packId, patch) => {
        const aplicar = (lista) => lista.map(c =>
            String(c.pack_id) === String(packId) ? { ...c, ...patch } : c
        );
        setConversas(prev => aplicar(prev));
        setKanban(prev => (prev ? {
            ...prev,
            colunas: Object.fromEntries(Object.entries(prev.colunas).map(([chave, col]) => [chave, { ...col, data: aplicar(col.data) }])),
        } : prev));
    };

    // No kanban por leitura, marcar lida/não lida troca a conversa de coluna.
    const reagruparPorLeitura = () => {
        if (visaoKanban && agruparKanban === 'leitura') carregarKanban();
    };

    const abrirConversa = (conversa) => {
        setSelecionada(conversa);
        setChatOpen(true);
    };

    // Marca/desmarca uma conversa individual como lida (atualização otimista).
    const alternarLeitura = async (conversa) => {
        const eraNaoLida = !!conversa.nao_lida;
        const ajusteContador = (delta) =>
            setMeta(m => ({ ...m, totalNaoLidas: Math.max(0, m.totalNaoLidas + delta) }));

        patchConversa(
            conversa.pack_id,
            eraNaoLida ? { nao_lida: false, qtd: 0 } : { nao_lida: true },
        );
        ajusteContador(eraNaoLida ? -1 : 1);

        try {
            if (eraNaoLida) {
                await marcarMensagemLida(conversa.pack_id);
            } else {
                await marcarMensagemNaoLida(conversa.pack_id);
            }
            reagruparPorLeitura();
        } catch {
            patchConversa(conversa.pack_id, { nao_lida: eraNaoLida, qtd: conversa.qtd });
            ajusteContador(eraNaoLida ? 1 : -1);
            toast({ title: 'Erro ao atualizar conversa', status: 'error' });
        }
    };

    // Finaliza / desfaz. A lista é recarregada para a conversa sair (ou voltar)
    // do filtro atual com a regra do servidor.
    const alternarFinalizacao = async (conversa) => {
        const eraFinalizada = !!conversa.finalizada;
        try {
            if (eraFinalizada) {
                await reabrirConversaMensageria(conversa.pack_id);
                toast({ title: 'Finalização desfeita', status: 'info', duration: 2000 });
            } else {
                await finalizarConversaMensageria(conversa.pack_id);
                toast({ title: 'Conversa finalizada', description: 'Reabre sozinha se o comprador escrever de novo.', status: 'success', duration: 2500 });
            }
            await recarregar();
        } catch {
            toast({ title: 'Erro ao atualizar conversa', status: 'error' });
        }
    };

    const copiarId = useCallback((label, value) => {
        const txt = String(value);
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(txt).catch(() => {});
        }
        toast({ title: `${label} copiado`, description: txt, status: 'success', duration: 1500 });
        // toast fora das deps de proposito (vide carregarConversas).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fecharChat = () => {
        setChatOpen(false);
        setSelecionada(null);
    };

    const handleMarcarTodasLidas = async () => {
        setLoadingMassa(true);
        try {
            await marcarTodasMensagensLidas();
            await recarregar();
            toast({ title: 'Todas marcadas como lidas', status: 'success' });
        } catch {
            toast({ title: 'Erro ao marcar conversas', status: 'error' });
        } finally {
            setLoadingMassa(false);
        }
    };

    const handleMarcarTodasNaoLidas = async () => {
        setLoadingMassa(true);
        try {
            await marcarTodasMensagensNaoLidas();
            await recarregar();
            toast({ title: 'Todas marcadas como não lidas', status: 'info' });
        } catch {
            toast({ title: 'Erro ao marcar conversas', status: 'error' });
        } finally {
            setLoadingMassa(false);
        }
    };

    const { total, totalPaginas, totalNaoLidas } = meta;
    // O kanban precisa de largura para as colunas; a lista segue com 1080px.
    const larguraMax = visaoKanban ? '1680px' : '1080px';

    const estadoVazio = (
        <Flex
            direction="column" align="center" justify="center"
            py={20} px={4} color="gray.300"
        >
            <Icon as={FaComments} boxSize={14} mb={4} />
            <Text fontSize="md" fontWeight="semibold" color="gray.500">
                {(filtroLeitura === 'nao_lidas' || filtroResposta === 'sem_responder')
                    ? 'Tudo em dia! 🎉'
                    : 'Nenhuma conversa encontrada'}
            </Text>
            <Text fontSize="sm" color="gray.400" mt={1} textAlign="center">
                {(filtroLeitura === 'nao_lidas' || filtroResposta === 'sem_responder')
                    ? 'Nenhuma conversa pendente no momento.'
                    : 'Tente ajustar a busca ou os filtros acima.'}
            </Text>
        </Flex>
    );

    const renderConversa = (c, compacto = false) => (
        <ConversaCard
            key={c.pack_id}
            conversa={c}
            compacto={compacto}
            selecionada={selecionada?.pack_id === c.pack_id}
            onClick={() => abrirConversa(c)}
            onCopiarId={copiarId}
            onToggleLeitura={() => alternarLeitura(c)}
            onToggleFinalizar={() => alternarFinalizacao(c)}
        />
    );

    return (
        // No kanban a página ocupa a largura toda: desconta a barra lateral
        // (64px no computador), como as outras telas largas.
        <Box bg="gray.50" minH="100vh" pb={10} ml={{ base: 0, md: visaoKanban ? '64px' : 0 }}>
            <ScrollToTop />

            {/* ─── HEADER ─── */}
            <Box maxW={larguraMax} mx="auto" px={{ base: 4, md: 6 }} pt={6} pb={3}>
                <Flex
                    justify="space-between"
                    align={{ base: 'stretch', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                >
                    <HStack spacing={3}>
                        <Flex
                            bg="orange.50" w={11} h={11} borderRadius="lg"
                            align="center" justify="center" flexShrink={0}
                        >
                            <Icon as={FaComments} color="#F68633" boxSize={5} />
                        </Flex>
                        <Box>
                            <HStack spacing={2}>
                                <Heading size="lg" color="gray.800">Mensageria</Heading>
                                {totalNaoLidas > 0 && (
                                    <Badge colorScheme="red" borderRadius="full" px={2} fontSize="11px">
                                        {totalNaoLidas} não lida{totalNaoLidas > 1 ? 's' : ''}
                                    </Badge>
                                )}
                            </HStack>
                            <Text fontSize="sm" color="gray.500">
                                Conversas com compradores do Mercado Livre
                            </Text>
                        </Box>
                    </HStack>

                    <Flex gap={2} wrap="wrap" align="center" justify={{ base: 'flex-start', md: 'flex-end' }}>
                        <AlternarVisao kanban={visaoKanban} onChange={setVisaoKanban} />
                        <Tooltip label="Marcar todas como lidas">
                            <Button
                                leftIcon={<FaEnvelopeOpen />} size="sm" colorScheme="green"
                                variant="outline" borderRadius="lg" onClick={handleMarcarTodasLidas}
                                isLoading={loadingMassa}
                            >
                                Marcar lidas
                            </Button>
                        </Tooltip>
                        <Tooltip label="Marcar todas como não lidas">
                            <Button
                                leftIcon={<FaEnvelope />} size="sm" colorScheme="orange"
                                variant="outline" borderRadius="lg" onClick={handleMarcarTodasNaoLidas}
                                isLoading={loadingMassa}
                            >
                                Não lidas
                            </Button>
                        </Tooltip>
                        <Tooltip label="Recarregar">
                            <IconButton
                                icon={loading ? <Spinner size="xs" /> : <FaSync />}
                                size="sm" variant="ghost" borderRadius="lg"
                                onClick={() => { recarregar(); carregarSla(); }}
                                isDisabled={loading} aria-label="Recarregar"
                            />
                        </Tooltip>
                    </Flex>
                </Flex>
            </Box>

            {/* ─── SLA de resposta (horas úteis: 09h–18h, dias úteis) ─── */}
            <Box maxW={larguraMax} mx="auto" px={{ base: 4, md: 6 }} pb={3}>
                <Flex gap={3} wrap="wrap" align="stretch">
                    <CartaoSla
                        titulo="Respondidas em até 2h úteis"
                        icone={FaCheckCircle}
                        carregando={loadingSla}
                        cor={sla?.percentual_no_prazo == null ? 'gray'
                            : sla.percentual_no_prazo >= 80 ? 'green'
                                : sla.percentual_no_prazo >= 50 ? 'orange' : 'red'}
                        valor={sla?.percentual_no_prazo == null ? '—' : `${String(sla.percentual_no_prazo).replace('.', ',')}%`}
                        detalhe={sla ? `${sla.no_prazo} de ${sla.base} no prazo${sla.abertas_fora_do_prazo ? ` · ${sla.abertas_fora_do_prazo} sem resposta atrasada${sla.abertas_fora_do_prazo > 1 ? 's' : ''}` : ''}` : 'Sem dados'}
                        nota={sla?.fora_da_conta ? `${sla.fora_da_conta} fora da conta` : null}
                        notaAjuda={'Mensagens que não entram no indicador: só "ok/obrigado", conversa que o Mercado Livre não deixa mais responder, e conversa finalizada antes de ter resposta. Sem isso a conta pareceria menor do que o movimento da tela.'}
                    />
                    <CartaoSla
                        titulo="Tempo médio de resposta"
                        icone={FaStopwatch}
                        carregando={loadingSla}
                        cor="blue"
                        valor={formatDuracao(sla?.tempo_medio_segundos)}
                        detalhe={sla ? `Em horas úteis · ${sla.respondidas} resposta${sla.respondidas !== 1 ? 's' : ''}` : 'Sem dados'}
                        nota={sla?.abertas_no_prazo ? `${sla.abertas_no_prazo} ainda no prazo` : null}
                        notaAjuda="Conversas sem resposta que ainda estão dentro das 2h úteis. Não contam como atraso nem entram na média."
                    />
                    <Flex flex="0 0 auto" direction="column" justify="center">
                        <CampoLabel>Período</CampoLabel>
                        <Select
                            size="sm" value={diasSla} onChange={e => setDiasSla(Number(e.target.value))}
                            bg="white" borderColor="gray.200" borderRadius="lg" w="140px"
                            _focus={{ borderColor: 'orange.300' }}
                            aria-label="Período das métricas"
                        >
                            <option value={7}>Últimos 7 dias</option>
                            <option value={30}>Últimos 30 dias</option>
                            <option value={90}>Últimos 90 dias</option>
                        </Select>
                    </Flex>
                </Flex>
                <Text fontSize="10px" color="gray.400" mt={1.5}>
                    Horas úteis: 09h às 18h, segunda a sexta, sem feriados. Agradecimentos e conversas finalizadas sem resposta ficam fora da conta.
                </Text>
            </Box>

            {/* ─── CARD: filtros + lista + paginação ─── */}
            <Box maxW={larguraMax} mx="auto" px={{ base: 4, md: 6 }}>
                <Box
                    bg="white"
                    borderRadius="xl"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.200"
                    overflow="hidden"
                >
                    <Box px={4} py={4}>
                        <Flex
                            gap={3}
                            direction={{ base: 'column', md: 'row' }}
                            align={{ base: 'stretch', md: 'flex-end' }}
                        >
                            <Box flex={1} maxW={{ md: '380px' }}>
                                <CampoLabel>Buscar</CampoLabel>
                                <InputGroup size="sm">
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Comprador, pack ou venda…"
                                        value={busca}
                                        onChange={e => setBusca(e.target.value)}
                                        bg="gray.50" borderWidth="1px" borderColor="gray.200"
                                        borderRadius="lg"
                                        _focus={{
                                            bg: 'white', borderColor: 'orange.300',
                                            boxShadow: '0 0 0 3px rgba(246,134,51,0.15)',
                                        }}
                                    />
                                </InputGroup>
                            </Box>
                            <Box>
                                <CampoLabel>Leitura</CampoLabel>
                                <Select
                                    size="sm" value={filtroLeitura} onChange={e => setFiltroLeitura(e.target.value)}
                                    bg="gray.50" borderColor="gray.200" borderRadius="lg"
                                    _focus={{ borderColor: 'orange.300' }}
                                    maxW={{ base: '100%', md: '160px' }}
                                >
                                    <option value="todas">Todas</option>
                                    <option value="nao_lidas">Não lidas</option>
                                    <option value="lidas">Lidas</option>
                                </Select>
                            </Box>
                            <Box>
                                <CampoLabel>Resposta</CampoLabel>
                                <Select
                                    size="sm" value={filtroResposta} onChange={e => setFiltroResposta(e.target.value)}
                                    bg="gray.50" borderColor="gray.200" borderRadius="lg"
                                    _focus={{ borderColor: 'orange.300' }}
                                    maxW={{ base: '100%', md: '170px' }}
                                >
                                    <option value="todas">Todas</option>
                                    <option value="sem_responder">Sem responder</option>
                                    <option value="respondidas">Respondidas</option>
                                    <option value="finalizadas">Finalizadas</option>
                                </Select>
                            </Box>
                            <Box>
                                <CampoLabel>Status da venda</CampoLabel>
                                <FiltroMultiplo
                                    size="sm"
                                    rotuloTodos="Todos os status"
                                    opcoes={OPCOES_STATUS}
                                    selecionados={filtroStatus}
                                    onChange={setFiltroStatus}
                                    largura={{ base: '100%', md: '190px' }}
                                    bg="gray.50"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                />
                            </Box>
                            <Box>
                                <CampoLabel>Ordenar</CampoLabel>
                                <Select
                                    size="sm" value={ordenar} onChange={e => setOrdenar(e.target.value)}
                                    bg="gray.50" borderColor="gray.200" borderRadius="lg"
                                    _focus={{ borderColor: 'orange.300' }}
                                    maxW={{ base: '100%', md: '210px' }}
                                >
                                    <option value="recentes">Mais recentes</option>
                                    <option value="sem_resposta">Aguardando há mais tempo</option>
                                    <option value="antigas">Mais antigas</option>
                                </Select>
                            </Box>
                        </Flex>
                    </Box>

                    <Divider />

                    {visaoKanban ? (
                        loading && !kanban ? (
                            <Flex justify="center" align="center" py={20}>
                                <Spinner color="orange.400" />
                            </Flex>
                        ) : !kanban?.total ? estadoVazio : (
                            <Box px={3} py={3} bg="gray.50">
                                <MensageriaKanban
                                    colunas={kanban.colunas}
                                    agrupar={agruparKanban}
                                    agruparDados={kanban.agrupar}
                                    onAgrupar={setAgruparKanban}
                                    total={kanban.total}
                                    isMobile={isMobile}
                                    porColuna={porColuna}
                                    onCarregarMais={(chave, carregadas) => setLimitesKanban(l => ({ ...l, [chave]: carregadas + porColuna }))}
                                    renderConversa={(c) => renderConversa(c, true)}
                                />
                            </Box>
                        )
                    ) : loading && conversas.length === 0 ? (
                        <Flex justify="center" align="center" py={20}>
                            <Spinner color="orange.400" />
                        </Flex>
                    ) : conversas.length === 0 ? estadoVazio : (
                        <VStack spacing={2.5} align="stretch" px={3} py={3}>
                            {conversas.map(c => renderConversa(c))}
                        </VStack>
                    )}

                    {!visaoKanban && <Divider />}

                    {!visaoKanban && <Flex px={4} py={3} justify="space-between" align="center" wrap="wrap" gap={2}>
                        <Text fontSize="xs" color="gray.500">
                            {total} conversa{total !== 1 ? 's' : ''}
                            {totalPaginas > 1 ? ` · página ${page} de ${totalPaginas}` : ''}
                        </Text>
                        <HStack spacing={1}>
                            <Button
                                size="xs" variant="outline" borderRadius="lg"
                                leftIcon={<ChevronLeftIcon />}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                isDisabled={page <= 1 || loading}
                            >
                                Anterior
                            </Button>
                            <Button
                                size="xs" variant="outline" borderRadius="lg"
                                rightIcon={<ChevronRightIcon />}
                                onClick={() => setPage(p => Math.min(totalPaginas, p + 1))}
                                isDisabled={page >= totalPaginas || loading}
                            >
                                Próxima
                            </Button>
                        </HStack>
                    </Flex>}
                </Box>
            </Box>

            {selecionada && (
                <ChatMensagensML
                    idOrigem={selecionada.id_origem || String(selecionada.pack_id)}
                    vendaId={selecionada.venda_id}
                    packId={String(selecionada.pack_id)}
                    isOpen={chatOpen}
                    onClose={fecharChat}
                    nomeCompradorInicial={selecionada.nome_comprador}
                    onMarcouLida={(packId) => { patchConversa(packId, { nao_lida: false, qtd: 0 }); reagruparPorLeitura(); }}
                    onStatusLeituraChange={(packId, naoLida) => {
                        patchConversa(packId, { nao_lida: naoLida, qtd: naoLida ? 1 : 0 });
                        reagruparPorLeitura();
                    }}
                />
            )}
        </Box>
    );
};

export default Mensageria;
